import { ReactElement, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';

interface SurveyGroupCompareLoadingBarProps {
  percent: number;
  visible: boolean;
  /** Main label (defaults to compare copy) */
  title?: string;
  /** Subtext under the bar */
  helperText?: string;
}

const DEFAULT_TITLE = 'Preparing compare charts…';
const DEFAULT_HELPER =
  'Loading survey results and question map. Progress is estimated until the server responds.';

const LOADING_CHECKLIST_MESSAGES = [
  'Warming up the calculators — they had a lie-in.',
  'Asking the database politely for your numbers.',
  'Crunching digits so you do not have to.',
  'Untangling the spaghetti of responses.',
  'Bribing the progress bar with imaginary biscuits.',
  'Final polish: teaching the charts to look modest.',
] as const;

const CHECKLIST_ROTATE_MS = 3200;

/** Checkmark on a light completed state; stroke uses current text colour. */
function CompletedCheckIcon(): ReactElement {
  return (
    <svg
      className="size-3.5 shrink-0 text-gray-600 animate-[checkmark-pop_0.28s_ease-out]"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Full-width animated progress strip for survey group views while group results / preview load.
 */
const SurveyGroupCompareLoadingBar = ({
  percent,
  visible,
  title = DEFAULT_TITLE,
  helperText = DEFAULT_HELPER,
}: SurveyGroupCompareLoadingBarProps): ReactElement | null => {
  const [checklistStep, setChecklistStep] = useState(0);

  useEffect(() => {
    if (!visible) {
      setChecklistStep(0);
      return undefined;
    }
    setChecklistStep(0);
    const id = window.setInterval(() => {
      setChecklistStep((s) => s + 1);
    }, CHECKLIST_ROTATE_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  const clamped = Math.min(100, Math.max(0, percent));
  const activeRow = checklistStep % LOADING_CHECKLIST_MESSAGES.length;
  /**
   * Use scaleX on a full-width strip so the gradient always paints (width:% fills can collapse
   * or fight min-width in some layouts / Tailwind gradient combinations).
   */
  const fillScale = Math.max(0.008, clamped / 100);

  return (
    <div
      className="relative z-20 mb-4 w-full min-w-0 rounded-xl border border-violet-200 bg-white bg-gradient-to-br from-violet-50 to-white px-4 py-6 shadow-md"
      role="status"
      aria-live="polite"
      aria-busy={clamped < 100}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-medium text-gray-800">
          {title}
        </p>
        <span className="tabular-nums text-sm font-semibold text-violet-700">
          {Math.round(clamped)}
          %
        </span>
      </div>
      <div className="relative h-3 w-full min-h-[12px] overflow-hidden rounded-full bg-gray-200">
        <div
          className="absolute inset-y-0 left-0 w-full origin-left rounded-full shadow-sm transition-transform duration-700 ease-out"
          style={{
            transform: `scaleX(${fillScale})`,
            background: 'linear-gradient(90deg, #7c3aed 0%, #6366f1 50%, #2563eb 100%)',
          }}
        />
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {helperText}
      </p>
      <div
        className="mt-5 border-t border-violet-100 pt-4"
        aria-label="Loading checklist"
      >
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-violet-600/90">
          While you wait
        </p>
        <ul
          className="space-y-2.5"
          role="list"
        >
          {LOADING_CHECKLIST_MESSAGES.map((msg, i) => {
            const done = i < activeRow;
            const active = i === activeRow;
            return (
              <li
                key={msg}
                className={cn(
                  'flex gap-3 rounded-lg px-1 py-0.5 transition-colors duration-300',
                  active && 'bg-violet-50/80',
                  done && 'opacity-80'
                )}
                aria-current={active ? 'step' : undefined}
              >
                <span
                  className={cn(
                    'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border text-violet-600 transition-colors duration-200',
                    done
                      ? 'border-gray-300 bg-gray-100 text-gray-600 shadow-sm'
                      : active
                        ? 'border-violet-400 bg-white'
                        : 'border-gray-200 bg-white'
                  )}
                  role="checkbox"
                  aria-checked={done}
                  aria-label={done ? 'Completed' : active ? 'In progress' : 'Pending'}
                >
                  {done ? (
                    <CompletedCheckIcon />
                  ) : active ? (
                    <Loader2 className="size-3.5 animate-spin text-violet-600" />
                  ) : null}
                </span>
                <span
                  className={cn(
                    'text-sm leading-snug text-gray-600',
                    active && 'font-medium text-gray-900',
                    done && 'text-gray-500'
                  )}
                >
                  {msg}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default SurveyGroupCompareLoadingBar;
